/**
 * 移动端通用视觉组件 —— 对齐 docs/操作人员移动端/操作人员移动端UI.html。
 *
 * - MobileAppBar   浅色毛玻璃顶栏（左标题 / 可选返回 / 右侧动作）
 * - IconBtn        34px 白底描边方形图标按钮（可带角标）
 * - StatGrid       三列统计卡（等宽数字 + 语义色）
 * - SegTabs        胶囊分段切换（选中项品牌渐变）
 * - SearchBox      白底圆角搜索框
 * - SectionHead    区块标题 + 计数徽标 + 「全部 ›」
 * - StatusPill     状态胶囊
 * - EmptyState     插画式空状态
 * - InfoCard       浅色提示卡（替代 MUI Alert 的现场提示）
 * - NodeHero       节点作业页顶部订单卡
 */
import { Box, ButtonBase, InputBase, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { T, TONE, type Tone } from '@/theme/tokens';

/* ------------------------------------------------------------------ */
/* 顶栏                                                                */
/* ------------------------------------------------------------------ */

interface MobileAppBarProps {
  title: string;
  /** 传入即显示左侧返回按钮 */
  onBack?: () => void;
  /** 右侧动作区 */
  right?: ReactNode;
}

export function MobileAppBar({ title, onBack, right }: MobileAppBarProps) {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        height: 52,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2.25,
        bgcolor: 'rgba(244,246,251,.86)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.line}`,
      }}
    >
      {onBack && (
        <IconBtn label="返回" onClick={onBack}>
          <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
        </IconBtn>
      )}
      <Typography
        component="h1"
        sx={{ flex: 1, minWidth: 0, fontSize: '18px', fontWeight: 700, letterSpacing: '-.01em', color: T.ink }}
        noWrap
      >
        {title}
      </Typography>
      {right && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>{right}</Box>}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 图标按钮                                                            */
/* ------------------------------------------------------------------ */

interface IconBtnProps {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  /** 红色角标数字（>0 才显示） */
  badge?: number;
  tone?: 'default' | 'brand';
}

export function IconBtn({ label, onClick, children, badge, tone = 'default' }: IconBtnProps) {
  const brand = tone === 'brand';
  return (
    <ButtonBase
      aria-label={label}
      onClick={onClick}
      sx={{
        position: 'relative',
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: '10px',
        bgcolor: brand ? T.brandSoft : '#fff',
        border: `1px solid ${brand ? T.brandLine : T.line}`,
        color: brand ? T.brand : T.ink2,
        '& .MuiSvgIcon-root': { fontSize: 19 },
        transition: 'transform .15s',
        '&:active': { transform: 'scale(.94)' },
      }}
    >
      {children}
      {badge != null && badge > 0 && <CountBadge n={badge} sx={{ top: -5, right: -5 }} />}
    </ButtonBase>
  );
}

/** 红色数字角标 */
export function CountBadge({ n, sx }: { n: number; sx?: SxProps<Theme> }) {
  return (
    <Box
      component="span"
      sx={[
        {
          position: 'absolute',
          minWidth: 16,
          height: 16,
          px: '4px',
          borderRadius: T.rPill,
          bgcolor: T.danger,
          color: '#fff',
          fontSize: '10px',
          fontWeight: 700,
          lineHeight: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid #fff',
          boxSizing: 'content-box',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {n > 99 ? '99+' : n}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 统计卡                                                              */
/* ------------------------------------------------------------------ */

export interface StatItem {
  label: string;
  value: ReactNode;
  tone: Tone;
  onClick?: () => void;
}

export function StatGrid({ items, sx }: { items: StatItem[]; sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={[
        { display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: '11px' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {items.map((s) => (
        <Box
          key={s.label}
          onClick={s.onClick}
          sx={{
            bgcolor: T.card,
            border: `1px solid ${T.line}`,
            borderRadius: T.rMd,
            py: '14px',
            px: 1.5,
            textAlign: 'center',
            boxShadow: T.shadowSm,
            cursor: s.onClick ? 'pointer' : 'default',
          }}
        >
          <Box
            sx={{
              fontFamily: T.mono,
              fontSize: '26px',
              fontWeight: 700,
              letterSpacing: '-.02em',
              lineHeight: 1,
              color: TONE[s.tone].fg,
              minHeight: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {s.value}
          </Box>
          <Typography sx={{ fontSize: '12px', color: T.ink2, mt: '7px', fontWeight: 500 }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 胶囊分段切换                                                        */
/* ------------------------------------------------------------------ */

export interface SegOption<V extends string> {
  value: V;
  label: string;
  count?: number;
}

interface SegTabsProps<V extends string> {
  value: V;
  options: SegOption<V>[];
  onChange: (v: V) => void;
  sx?: SxProps<Theme>;
}

export function SegTabs<V extends string>({ value, options, onChange, sx }: SegTabsProps<V>) {
  return (
    <Box
      role="tablist"
      sx={[
        {
          display: 'flex',
          gap: '6px',
          bgcolor: '#fff',
          border: `1px solid ${T.line}`,
          borderRadius: T.rPill,
          p: '5px',
          boxShadow: T.shadowSm,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            sx={{
              flex: 1,
              py: '9px',
              borderRadius: T.rPill,
              fontFamily: 'inherit',
              fontSize: '13.5px',
              fontWeight: 600,
              color: on ? '#fff' : T.ink2,
              background: on ? T.brandGrad : 'transparent',
              boxShadow: on ? '0 4px 12px rgba(31,111,229,.30)' : 'none',
              transition: 'all .16s',
            }}
          >
            {o.label}
            {o.count != null && (
              <Box component="span" sx={{ fontFamily: T.mono, fontWeight: 700, ml: '3px', fontSize: '12.5px' }}>
                {o.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 搜索框                                                              */
/* ------------------------------------------------------------------ */

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        height: 44,
        bgcolor: '#fff',
        border: `1px solid ${T.line}`,
        borderRadius: T.rMd,
        px: '14px',
        boxShadow: T.shadowSm,
        transition: 'border-color .18s, box-shadow .18s',
        '&:focus-within': { borderColor: T.brand, boxShadow: `0 0 0 3px ${T.brandSoft}` },
      }}
    >
      <SearchIcon sx={{ fontSize: 19, color: T.ink3 }} />
      <InputBase
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        sx={{ fontSize: '14px', color: T.ink, '& input::placeholder': { color: T.ink4, opacity: 1 } }}
      />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 区块标题                                                            */
/* ------------------------------------------------------------------ */

export function SectionHead({
  title,
  count,
  moreText,
  onMore,
}: {
  title: string;
  count?: number;
  moreText?: string;
  onMore?: () => void;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mx: '2px', mt: '4px', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: T.ink }}>{title}</Typography>
        {count != null && (
          <Box
            component="span"
            sx={{ fontSize: '12px', fontWeight: 700, color: T.brand, bgcolor: T.brandSoft, px: '9px', py: '3px', borderRadius: T.rPill }}
          >
            {count}
          </Box>
        )}
      </Box>
      {moreText && (
        <ButtonBase onClick={onMore} sx={{ fontSize: '12.5px', color: T.ink3, fontWeight: 500, fontFamily: 'inherit' }}>
          {moreText}
        </ButtonBase>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 状态胶囊                                                            */
/* ------------------------------------------------------------------ */

export function StatusPill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: '11.5px',
        fontWeight: 700,
        px: '10px',
        py: '4px',
        borderRadius: T.rPill,
        bgcolor: TONE[tone].bg,
        color: TONE[tone].ink,
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
      }}
    >
      {children}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 空状态                                                              */
/* ------------------------------------------------------------------ */

export function EmptyState({ icon, title, desc }: { icon: ReactNode; title: string; desc?: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: '72px', px: 5 }}>
      <Box
        sx={{
          width: 104,
          height: 104,
          borderRadius: '28px',
          background: 'linear-gradient(135deg,#EAF2FE,#F4F8FE)',
          border: `1px solid ${T.brandLine}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '22px',
          color: T.brand,
          '& .MuiSvgIcon-root': { fontSize: 46 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: T.ink }}>{title}</Typography>
      {desc && (
        <Typography sx={{ fontSize: '13px', color: T.ink3, mt: 1, lineHeight: 1.6, maxWidth: 230 }}>{desc}</Typography>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 提示卡                                                              */
/* ------------------------------------------------------------------ */

export function InfoCard({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '11px',
        p: '14px',
        borderRadius: T.rMd,
        bgcolor: T.card,
        border: `1px solid ${T.line}`,
        boxShadow: T.shadowSm,
        mb: 1.5,
        fontSize: '13px',
        lineHeight: 1.6,
        color: T.ink2,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: '7px',
          bgcolor: T.brandSoft,
          color: T.brand,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: '1px',
          '& .MuiSvgIcon-root': { fontSize: 15 },
        }}
      >
        {icon ?? <InfoOutlinedIcon />}
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 节点作业页 · 订单卡                                                 */
/* ------------------------------------------------------------------ */

export function NodeHero({ orderNo, nodeName, lastNode }: { orderNo: string; nodeName: string; lastNode?: boolean }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: T.rLg,
        bgcolor: T.card,
        border: `1px solid ${T.line}`,
        boxShadow: T.shadow,
        mb: '14px',
      }}
    >
      <Typography sx={{ fontSize: '12px', color: T.ink3, fontWeight: 600, letterSpacing: '.04em' }}>订单</Typography>
      <Typography
        sx={{ fontFamily: T.mono, fontSize: '15px', fontWeight: 600, color: T.ink, mt: '7px', wordBreak: 'break-all', lineHeight: 1.4 }}
      >
        {orderNo}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          mt: '11px',
          fontSize: '13px',
          color: T.ink2,
          bgcolor: T.line2,
          px: 1.5,
          py: 1,
          borderRadius: T.rSm,
        }}
      >
        <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: T.blue, flexShrink: 0 }} />
        <span>
          当前节点 · <Box component="b" sx={{ color: T.ink, fontWeight: 600 }}>{nodeName}{lastNode ? '（末节点）' : ''}</Box>
        </span>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 白卡底座                                                            */
/* ------------------------------------------------------------------ */

export const cardSx = {
  bgcolor: T.card,
  border: `1px solid ${T.line}`,
  borderRadius: T.rLg,
  boxShadow: T.shadow,
} as const;
